import type { TemperatureBand } from "@ui/utils/temperature-bands";

/**
 * Canonical value sets for the destinations filters. Deliberately free of
 * React/assets so both the filter components and the URL codec
 * (`search-params.ts`, unit-tested under node) consume one source of truth.
 */

/**
 * Monthly family budget, in USD cents. The ranking hinges affordability on this
 * number rather than filtering on it, so the bounds only have to cover the range
 * a family might name — the top of it reads as "no ceiling", because for this
 * corpus ten thousand dollars a month and no limit are the same answer.
 *
 * Re-derived against the composed panel when `destinations_costs_writer`
 * flipped (n=25 dev destinations, p50 totals $1,177.31–$6,072.01/month, median
 * $2,406.08): the whole span sits inside these bounds and occupies the lower
 * ~60% of the histogram, so nothing bunches or clips. The floor stays below the
 * corpus — families name budgets cheaper than any city we have priced, and
 * production holds cheaper cities than dev does. Retuning for production is a
 * change to these three numbers and nothing else.
 */
export const BUDGET_MIN_CENTS = 50_000;
export const BUDGET_MAX_CENTS = 1_000_000;
export const BUDGET_STEP_CENTS = 5_000;

/** Bar count of the budget distribution histogram — enough that each clustered
 *  bar renders ~8px wide in the lg filters modal. The bucket width is derived,
 *  so retuning the budget bounds can never desync count and interval. */
export const BUDGET_HISTOGRAM_BAR_COUNT = 50;
export const BUDGET_HISTOGRAM_INTERVAL_CENTS = (BUDGET_MAX_CENTS - BUDGET_MIN_CENTS) / BUDGET_HISTOGRAM_BAR_COUNT;

export type KoppenMainClass = "A" | "B" | "C" | "D" | "E";

export const KOPPEN_MAIN_CLASSES: readonly KoppenMainClass[] = ["A", "B", "C", "D", "E"];

export const TEMPERATURE_BANDS: readonly TemperatureBand[] = ["cool", "mild", "warm", "hot"];

export type LanguageCode =
  | "ar"
  | "cs"
  | "da"
  | "de"
  | "el"
  | "en"
  | "es"
  | "fi"
  | "fr"
  | "hi"
  | "hu"
  | "id"
  | "it"
  | "ja"
  | "ko"
  | "nl"
  | "no"
  | "pl"
  | "pt"
  | "ru"
  | "sv"
  | "th"
  | "tr"
  | "vi"
  | "zh";

export const LANGUAGE_OPTIONS: readonly LanguageCode[] = [
  "ar",
  "cs",
  "da",
  "de",
  "el",
  "en",
  "es",
  "fi",
  "fr",
  "hi",
  "hu",
  "id",
  "it",
  "ja",
  "ko",
  "nl",
  "no",
  "pl",
  "pt",
  "ru",
  "sv",
  "th",
  "tr",
  "vi",
  "zh",
];

export type UtcOffset =
  | -12
  | -11
  | -10
  | -9
  | -8
  | -7
  | -6
  | -5
  | -4
  | -3
  | -2
  | -1
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14;

export const UTC_OFFSETS: readonly UtcOffset[] = [
  -12, -11, -10, -9, -8, -7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
];
